import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ApiError, requestOtp, verifyOtp } from "../lib/api.ts";
import { setOwner, setToken } from "../lib/auth.ts";

const RESEND_COOLDOWN_SEC = 60;

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // 再送クールダウン: 1 秒ごとにデクリメント
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  const sendOtp = async (targetEmail: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await requestOtp(targetEmail);
      setNotice(res.message || t("login.otp_sent"));
      setStep("otp");
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRequest = (e: React.FormEvent) => {
    e.preventDefault();
    void sendOtp(email.trim().toLowerCase());
  };

  const handleResend = () => {
    if (cooldown > 0 || busy) return;
    void sendOtp(email.trim().toLowerCase());
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await verifyOtp(email.trim().toLowerCase(), otp.trim());
      setToken(res.access_token);
      setOwner({ email: res.owner.email, display_name: res.owner.display_name });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "LOCKED") {
        setError(t("login.locked"));
      } else if (err instanceof ApiError && err.code === "INVALID_OTP") {
        setError(t("login.wrong_otp"));
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="nx-login-page nx-login-page--centered">
      <main className="nx-login-page__main">
        <section className="nx-login-card">
          <p className="nx-label">{t("login.label")}</p>
          <h1 className="nx-login-card__heading">{t("login.title")}</h1>
          <p className="nx-login-card__subtitle">{t("login.subtitle")}</p>

        {step === "email" && (
          <form onSubmit={handleRequest} className="nx-login-form">
            <label className="nx-login-field">
              <span>{t("login.email")}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
              />
            </label>
            {error && <p className="nx-login-msg nx-login-msg--error">{error}</p>}
            <button type="submit" className="nx-login-btn" disabled={busy || !email}>
              {busy ? t("common.loading") : t("login.send_otp")}
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={handleVerify} className="nx-login-form">
            {notice && <p className="nx-login-msg nx-login-msg--notice">{notice}</p>}
            <label className="nx-login-field">
              <span>{t("login.otp_label")}</span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                required
                autoFocus
                pattern="\d{6}"
              />
            </label>
            {error && <p className="nx-login-msg nx-login-msg--error">{error}</p>}
            <button type="submit" className="nx-login-btn" disabled={busy || otp.length !== 6}>
              {busy ? t("common.loading") : t("login.verify")}
            </button>
            <div className="nx-login-actions">
              <button
                type="button"
                className="nx-login-btn--outline"
                onClick={handleResend}
                disabled={busy || cooldown > 0}
              >
                {cooldown > 0
                  ? t("login.resend_in", { seconds: cooldown, defaultValue: "再送する ({{seconds}}秒)" })
                  : t("login.resend")}
              </button>
              <button
                type="button"
                className="nx-login-btn--ghost"
                onClick={() => {
                  setStep("email");
                  setOtp("");
                  setError(null);
                  setNotice(null);
                  setCooldown(0);
                }}
                disabled={busy}
              >
                {t("login.change_email", { defaultValue: "メールアドレスを変更" })}
              </button>
            </div>
          </form>
        )}

        <footer className="nx-login-footer">
          <Link to="/terms" className="nx-login-link">
            {t("login.footer_terms")}
          </Link>
          <span className="nx-text-secondary"> · </span>
          <Link to="/privacy" className="nx-login-link">
            {t("login.footer_privacy")}
          </Link>
        </footer>
        </section>
      </main>
    </div>
  );
}
